import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import dayjs from 'dayjs';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, share, switchMap, tap } from 'rxjs/operators';
import { PayrollTimecardFeatureFlags } from 'src/app/shared/models/enums/team-feature-flags/payroll-timecard-feature-flags.enum';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { UnsubscribeOnDestroy } from 'src/app/shared/utilities/virtual-images/unsubscribe-ondestroy';
import { EffectiveDatesService } from 'src/app/travel-contracts/effective-dates/effective-dates.service';
import { BillingPolicy } from 'src/app/travel-contracts/effective-dates/model/billing-policy.model';
import { BillingRates } from 'src/app/travel-contracts/effective-dates/model/billing-rates.model';
import { TimeTrackingStatus } from 'src/app/travel-contracts/effective-dates/model/time-tracking-status.enum';

@UntilDestroy()
@Component({
    selector: 'effective-dates-history',
    templateUrl: './effective-dates-history.component.html',
    styleUrls: ['./effective-dates-history.component.scss']
})
export class EffectiveDatesHistoryComponent extends UnsubscribeOnDestroy implements OnInit {
    data: BillingRates;
    load$ = new BehaviorSubject(true);
    initialData: BillingRates;
    invalidDates = ['1900-01-01T00:00:00', '0001-01-01T00:00:00'];
    rateSections = ['Bill Rates'];
    ruleSections = ['Overtime Rules', 'Doubletime Rules'];
    effectiveDateDeleted: boolean;
    editMode: boolean;
    fieldsMapping = {
        billRegularRate: 'Regular',
        billOtRate: 'Overtime',
        billDtRate: 'Doubletime',
        billChargeRate: 'Charge',
        billOnCallRate: 'On Call',
        billCallBackRate: 'Call Back',
        billHolidayRate: 'Holiday',
        billOnCall24HourRate: '24hr Call (Daily)',
        billNightCallRate: 'Night Call (Daily)',
        billWeekendRate: 'Weekend Call (Daily)',
        agencyRegularRate: 'Regular',
        agencyOTRate: 'Overtime',
        agencyDTRate: 'Doubletime',
        agencyChargeRate: 'Charge',
        agencyOnCallRate: 'On Call',
        agencyCallBackRate: 'Call Back',
        agencyHolidayRate: 'Holiday',
        agencyOnCall24HourRate: '24hr Call (Daily)',
        agencyNightCallRate: 'Night Call (Daily)',
        agencyWeekendRate: 'Weekend Call (Daily)',
        otDayRule: 'in a day',
        otWeekRule: 'in a week',
        oT2WeekRule: 'in 2 weeks',
        dtDayRule: 'in a day',
        dtWeekRule: 'in a week',
        dT2WeekRule: 'in 2 weeks'
    };

    pendingSave: boolean;
    outOfRangeDateError = 'Date is outside the contract start and end dates';
    isUsedDatedError = 'Date is already in use';
    displayLocumPayrollEffectiveDatesHistoryFlag = false;

    constructor(
        public dialogRef: MatDialogRef<EffectiveDatesHistoryComponent>,
        private readonly _effectiveDatesService: EffectiveDatesService,
        private readonly _toasterService: ToasterService,
        private readonly _confirmDialogService: ConfirmDialogService,
        protected readonly _lDFeatureManager: LDFeatureManager,
        @Inject(MAT_DIALOG_DATA)
        public dialogData: {
            contractId: number;
            isDisplayAgencyPay: boolean;
            contractStartDate: string;
            contractEndDate: string;
            editMode: boolean;
            isLocum: boolean;
        }
    ) {
        super();
    }

    ngOnInit(): void {
        this._lDFeatureManager
            .isEnabled(PayrollTimecardFeatureFlags.DisplayLocumPayrollEffectiveDatesHistory)
            .pipe(untilDestroyed(this))
            .subscribe((locumsFlagIsEnabled) => {
                this.displayLocumPayrollEffectiveDatesHistoryFlag = locumsFlagIsEnabled;
            });

        this.load$
            .pipe(
                untilDestroyed(this),
                switchMap(() => this._effectiveDatesService.getEffectiveDatesHistory(this.dialogData.contractId)),
                tap((res) => {
                    this.data = this.formatBillingData(res);
                    this.initialData = JSON.parse(JSON.stringify(res));
                }),
                catchError(() => {
                    this._toasterService.toast('Error occurred while getting the data');
                    return of({} as BillingRates);
                }),
                share()
            )
            .subscribe();

        if (this.dialogData.isDisplayAgencyPay) {
            this.rateSections.push('Agency Pay');
        }

        this.editMode = this.dialogData.editMode;
    }

    get minDate() {
        return dayjs(this.dialogData.contractStartDate).toDate();
    }

    get maxDate() {
        return dayjs(this.dialogData.contractEndDate).toDate();
    }

    saveChanges(): void {
        //if no changes just close modal
        if (!this.data.billingPolicies.some((b) => b.isUpdated)) {
            this.close(true);
        }

        this.pendingSave = true;
        this.data.billingPolicies
            .filter((b) => b.isUpdated)
            .forEach((element) => {
                this._effectiveDatesService
                    .updateBillingRate(this.data.contractId, this.deformatData(element))
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: () => {
                            this.close(true);
                            this._toasterService.toast('Changes have been saved');
                        },
                        error: () => {
                            this._toasterService.toast('Error occurred while saving');
                        },
                        complete: () => {
                            this.pendingSave = false;
                        }
                    });
            });
    }

    deformatData(el) {
        for (const [key, value] of Object.entries(this.fieldsMapping)) {
            const rateObj = el.rates.find((i) => i.payType === value && i.billType === this.getBillType(key));
            el[key] = rateObj ? rateObj.newRate : el[key];
        }
        return el;
    }

    getBillType(val: string): string {
        if (val.startsWith('bill')) {
            return 'bill';
        }
        if (val.startsWith('agency')) {
            return 'agency';
        }
        if (val.toLowerCase().startsWith('ot')) {
            return 'overtime';
        }
        if (val.toLowerCase().startsWith('dt')) {
            return 'doubletime';
        }
        return '';
    }

    close(isSaved = false): void {
        this.dialogRef.close(isSaved || this.effectiveDateDeleted);
    }

    formatBillingData(data: BillingRates): BillingRates {
        if (!data.billingPolicies?.length) {
            return data;
        }

        const currentRateObj = data.billingPolicies.find(
            (item) => item.timeTrackingStatus === TimeTrackingStatus.Current
        );
        const pastRateObjs = data.billingPolicies.filter(
            (item) =>
                item.timeTrackingStatus === TimeTrackingStatus.Past &&
                item.endDate >=
                    (this.invalidDates.includes(item.startDate.toString()) ? data.contractStartDate : item.startDate)
        );
        const futureRateObjs = data.billingPolicies.filter(
            (item) => item.timeTrackingStatus === TimeTrackingStatus.Future
        );

        const filteredData: BillingRates = {
            ...data,
            billingPolicies: []
        };

        if (pastRateObjs) {
            filteredData.billingPolicies = filteredData.billingPolicies.concat(pastRateObjs);
        }
        if (currentRateObj) {
            filteredData.billingPolicies.push(currentRateObj);
        }
        if (futureRateObjs) {
            filteredData.billingPolicies = filteredData.billingPolicies.concat(futureRateObjs);
        }

        return {
            ...filteredData,
            billingPolicies: filteredData.billingPolicies.map((rate, index) => {
                const prevObj = filteredData.billingPolicies[index - 1];
                rate.rates = Object.entries(this.fieldsMapping).reduce((rates, [key, value]) => {
                    return [
                        ...rates,
                        {
                            payType: value,
                            billType: this.getBillType(key),
                            oldRate: prevObj ? prevObj[key] : 0,
                            newRate: rate[key]
                        }
                    ];
                }, []);

                if (index === 0 && this.invalidDates.includes(rate.startDate.toString())) {
                    rate.startDate = data.contractStartDate;
                    rate.isDateDisabled = true;
                }

                return rate;
            })
        };
    }

    getTimeTrackingName(id: number): string {
        switch (id) {
            case TimeTrackingStatus.Past:
                return 'Past';
            case TimeTrackingStatus.Current:
                return 'Current';
            case TimeTrackingStatus.Future:
                return 'Future';
            default:
                return '';
        }
    }

    getRates(section: any, data: any) {
        const showLocums1099Rates = this.dialogData?.isLocum && this.displayLocumPayrollEffectiveDatesHistoryFlag;
        return this._effectiveDatesService.getRates(section, data, showLocums1099Rates);
    }

    onRateChanged(event: Event, policyIndex: number, rateIndex: number): void {
        const value: number = (event.target as HTMLInputElement).valueAsNumber;
        const policyToUpdate = this.data.billingPolicies[policyIndex];
        if (policyToUpdate && policyToUpdate.rates[rateIndex].oldRate !== value) {
            policyToUpdate.rates[rateIndex].oldRate = value;
            policyToUpdate.isUpdated = true;
        }
    }

    isBillSection(section): boolean {
        return this._effectiveDatesService.isBillSection(section);
    }

    toFormattedDate(event) {
        const value = event.targetElement.value;
        if (!value.match(/\//g)) {
            return '';
        }
        if (value.match(/\//g).length !== 1) {
            return event.value;
        }
        const fullDate = new Date(value);
        const month = fullDate.getMonth() + 1;
        const day = fullDate.getDate();
        const currentYear = new Date().getFullYear();

        return new Date(`${month}/${day}/${currentYear}`);
    }

    isOutOfRangeDate(value): boolean {
        const date = dayjs(value).format();
        const contractStartDateString = dayjs(this.dialogData.contractStartDate).format();
        const contractEndDateString = dayjs(this.dialogData.contractEndDate).format();
        return value && (date < contractStartDateString || date > contractEndDateString);
    }

    isSaveDisabled(): boolean {
        return (
            !!this.data?.billingPolicies.find(
                (item) => !item.startDate || this.isOutOfRangeDate(item.startDate) || item.hasUsedDateError
            ) || this.pendingSave
        );
    }

    onDateChanged(policyIndex: number): void {
        const policyToUpdate = this.data.billingPolicies[policyIndex];
        policyToUpdate.hasUsedDateError = this.isDateAlreadyUsed();
        policyToUpdate.hasOutOfRangeDateError = this.isOutOfRangeDate(policyToUpdate.startDate);
        policyToUpdate.errorMessage =
            policyToUpdate.hasOutOfRangeDateError && policyToUpdate.hasUsedDateError
                ? this.outOfRangeDateError + '\n' + this.isUsedDatedError
                : policyToUpdate.hasOutOfRangeDateError
                  ? this.outOfRangeDateError
                  : policyToUpdate.hasUsedDateError
                    ? this.isUsedDatedError
                    : '';
        if (policyToUpdate && !policyToUpdate.hasUsedDateError && !policyToUpdate.hasOutOfRangeDateError) {
            policyToUpdate.isUpdated = true;
        }
    }

    openDeleteDialog(item: BillingPolicy): void {
        const startDate = this.toDateString(this.initialData.billingPolicies.find((i) => i.id === item.id)?.startDate);
        const statusName = this.getTimeTrackingName(item.timeTrackingStatus);
        const incudeAgencyPay = this.rateSections.length > 1;
        const message =
            `Please confirm that you'd like to delete all rates with a&nbsp;${statusName.toLocaleLowerCase()} effective date of <b>${startDate}</b>.` +
            `<br><br>This includes:` +
            `<br>- Bill Rates` +
            (incudeAgencyPay ? `<br>- Agency Pay` : ``) +
            `<br>- Overtime Rules` +
            `<br>- Doubletime Rules`;
        const action = 'YES, DELETE';
        const title = `Delete ${statusName} Effective Date: ${startDate}`;

        this._confirmDialogService
            .showDialog({ message, action, title, needExtraPadding: false, autoFocus: false })
            .pipe(
                untilDestroyed(this),
                catchError(() => of())
            )
            .subscribe((isDelete) => {
                if (isDelete) {
                    this._effectiveDatesService
                        .deleteEffectiveDate(item.id)
                        .pipe(untilDestroyed(this))
                        .subscribe({
                            next: () => {
                                this._toasterService.toast(`Effective Date ${startDate} has been deleted`);
                                this.load$.next(true);
                                this.effectiveDateDeleted = true;
                            },
                            error: () => {
                                this._toasterService.toast(`Error occurred when deleting Effective Date ${startDate}`);
                            }
                        });
                }
            });
    }

    toDateString(date: Date) {
        return dayjs(date).format('MM/DD/YY');
    }

    isDateAlreadyUsed(): boolean {
        this.data.billingPolicies.forEach((t) => (t.hasUsedDateError = false));
        return this.hasDuplicates(this.data.billingPolicies);
    }

    private hasDuplicates(policy: BillingPolicy[]): boolean {
        const seen = new Set();

        for (const item of policy) {
            const startDate = new Date(item.startDate).getTime();

            if (seen.has(startDate)) {
                return true;
            }

            seen.add(startDate);
        }
        return false;
    }
}
